import { ChannelKind, IChannel } from '@common/interfaces/channels'
import { stableHash } from './stableHash'

/**
 * Defaults for a group that has not picked an emoji. No 💬: that was the one glyph
 * every such group shared, which is what made them indistinguishable.
 */
// prettier-ignore
export const GROUP_EMOJI_DEFAULTS = [
  '🚀', '📣', '🌱', '🔥', '🎯', '💡', '📚', '🧭', '🧩', '🎨',
  '🌊', '🍀', '🌻', '🍋', '🍇', '🐝', '🦊', '🐙', '🦉', '🐳',
  '🌵', '🍄', '🎈', '💎', '🔔', '🏆', '🎲', '🧪', '🌈', '🍉',
  '🍒', '🥑', '🐢', '🦋', '🐬', '🦁', '🐼', '🌙', '⚡', '📦',
]

/** Group id → the glyph it is shown with. */
export type GroupEmojiMap = Record<string, string>

type IGroupEmojiSource = Pick<IChannel, 'id' | 'emoji' | 'createdAt'>

const preferredSlot = (groupId: string): number =>
  (stableHash(groupId) >>> 0) % GROUP_EMOJI_DEFAULTS.length

const firstFreeSlot = (preferred: number, taken: Set<number>): number => {
  for (let offset = 0; offset < GROUP_EMOJI_DEFAULTS.length; offset++) {
    const slot = (preferred + offset) % GROUP_EMOJI_DEFAULTS.length
    if (!taken.has(slot)) return slot
  }
  // More groups than defaults: share a glyph rather than show none.
  return preferred
}

/** A group's own emoji, with blank and whitespace-only treated as unset. */
export const groupEmoji = (emoji: string | null | undefined, fallback: string): string =>
  emoji?.trim() || fallback

/**
 * A default for every group on the list, no two alike while the palette lasts.
 *
 * Oldest first by `createdAt`, each takes its hashed slot or the next free one: a newly
 * created group never displaces an existing default, but joining an older group (or one
 * leaving archive) can. Explicit picks don't reserve slots, so they never move another's.
 */
export const assignGroupEmojiDefaults = (groups: IGroupEmojiSource[]): GroupEmojiMap => {
  const byAge = [...groups].sort(
    (a, b) => a.createdAt.localeCompare(b.createdAt) || a.id.localeCompare(b.id),
  )
  const taken = new Set<number>()
  const defaults: GroupEmojiMap = {}
  byAge.forEach((group) => {
    const slot = firstFreeSlot(preferredSlot(group.id), taken)
    taken.add(slot)
    defaults[group.id] = GROUP_EMOJI_DEFAULTS[slot]
  })
  return defaults
}

/** Every group's glyph: its own emoji, else its default. */
export const resolveGroupEmojis = (
  groups: IGroupEmojiSource[],
  defaults: GroupEmojiMap = assignGroupEmojiDefaults(groups),
): GroupEmojiMap =>
  Object.fromEntries(groups.map((group) => [group.id, groupEmoji(group.emoji, defaults[group.id])]))

/**
 * A group's glyph from `emojis`, for a group that may not be on the list (one opened
 * from Browse): then its own emoji, else its hashed default — unprobed, so it can match
 * a listed group's.
 */
export const groupEmojiOf = (
  groupId: string,
  emojis: GroupEmojiMap,
  ownEmoji?: string | null,
): string => emojis[groupId] ?? groupEmoji(ownEmoji, GROUP_EMOJI_DEFAULTS[preferredSlot(groupId)])

/**
 * A thread's group glyph, or null while its group is off the list (not loaded yet, or
 * archived): a thread doesn't carry its group's emoji, so a default there would be a guess.
 */
export const threadGroupEmoji = (parentChannelId: string, emojis: GroupEmojiMap): string | null =>
  emojis[parentChannelId] ?? null

/** The glyph a conversation's header shows: its group's, and none for a DM or group DM. */
export const conversationGroupEmoji = (
  channel: Pick<IChannel, 'id' | 'kind' | 'parentChannelId' | 'emoji'>,
  emojis: GroupEmojiMap,
): string | null => {
  if (channel.kind !== ChannelKind.Channel) return null
  return channel.parentChannelId
    ? threadGroupEmoji(channel.parentChannelId, emojis)
    : groupEmojiOf(channel.id, emojis, channel.emoji)
}
